// @ts-nocheck
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns, resolveSelectValue } from '../../common/Table';

import {
  CommaNumber_NoDecimal,
  WorkflowWithJustification,
} from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import { RequestStatusLabel } from './RequestStatusLabel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings.ts';
import GameLink from '../../common/links/GameLink';
import UserLink from '../../common/links/UserLink';
import requestStatusNames from '../../../constants/requestStatusNames';
import PlatformLink from '../../common/links/PlatformLink';
import { useTranslation } from 'react-i18next';
import {
  exportMyRequestData,
  getRequestsGrouped,
  getRequestsMadeByMeGrouped,
} from '../../../api/requestApi';
import { useAppSelector } from '../../../hooks';

export interface RequestsMadeByMeGroupedListProps {
  reload?: any;
  settingsKey?: string;
  tableViewSwitcher?: any;
  allRequests?: boolean;
}

function RequestsMadeByMeGroupedList({
  reload,
  settingsKey,
  tableViewSwitcher,
  allRequests,
}: RequestsMadeByMeGroupedListProps) {
  const location = useLocation();
  const { t } = useTranslation();

  const currentUser = useAppSelector((state) => state.auth);

  const allColumns = [
    'reference',
    'games',
    'platform',
    'numberOfKeys',
    'keyWorkflowName',
    'requestedByFullName',
    'requestedDate',
    'currentUserProfileGroupName',
    'requestStatus',
  ];
  const defaultVisibleColumns = allColumns;

  const [requests, setRequests] = useState();
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    settingsKey || 'requestsmadebymelist-2.13-settings',
    getTableSettingsDefaults({
      defaultSort: 'reference',
      direction: 'desc',
      defaultVisibleColumns,
    }),
    null,
    allRequests ? getRequestsGrouped : getRequestsMadeByMeGrouped,
    setRequests,
    reload,
    t('Failed to load requests')
  );

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig = [
    {
      Key: 'reference',
      Label: '#',
      width: '5%',
      className: 'no-wrap',
      Render: (row) => <Link to={'/request/' + row.id}>{row.reference}</Link>,
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'games',
      Label: t('Games'),
      width: '20%',
      Render: (row) =>
        row.games && row.games.length > 0
          ? row.games.map((x, i) => (
              <GameLink
                gameId={x.id}
                skuId={x.skuId}
                gameTitle={x.gameTitle}
                skuName={x.skuName}
                currentUser={currentUser}
                key={`${row.id},${x.skuId}`}
                className={i < row.games.length - 1 ? 'mb-4' : ''}
              />
            ))
          : '',
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'platform',
      Label: t('Platform'),
      width: '20%',
      Render: (row) =>
        row.games && row.games.length > 0
          ? row.games.map((x, i) => (
              <PlatformLink
                id={x.platformId}
                name={x.platform}
                key={`${row.id},${x.platformId}`}
                className={i < row.games.length - 1 ? 'mb-4' : ''}
                currentUser={currentUser}
              />
            ))
          : '',
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'numberOfKeys',
      Label: t('Keys'),
      width: '10%',
      className: 'text-right num no-wrap',
      Render: (row) => CommaNumber_NoDecimal(row.numberOfKeys),
    },
    {
      Key: 'keyWorkflowName',
      Label: t('Type'),
      width: '20%',
      Render: (row) => WorkflowWithJustification(row),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'requestedByFullName',
      Label: t('Requested by'),
      width: '10%',
      Render: (row) => (
        <>
          <UserLink id={row.requestedById} name={row.requestedByFullName} />
          <br />
          {row.requestedByCompanyName}
        </>
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'currentUserProfileGroupName',
      Label: t('Waiting for'),
      width: '10%',
      Render: (row) =>
        row.currentUserProfileGroupNames &&
        row.currentUserProfileGroupNames.length > 0
          ? row.currentUserProfileGroupNames.map((x) => (
              <span key={x}>{x}</span>
            ))
          : '',
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'requestStatus',
      Label: t('Status'),
      width: '15%',
      Render: (row) => (
        <RequestStatusLabel
          requestStatus={row.requestStatus}
          isWaitingForKeys={row.isWaitingForKeys}
          isDownloaded={row.downloaded}
          isEmailSent={row.allAccountDetailsEmailsSent}
        />
      ),
      Filter: {
        Render: (col) => {
          const rawValue = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          const value = resolveSelectValue(rawValue, requestStatusNames);
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, el.target.value)}
            >
              <option value="">All</option>
              {requestStatusNames.map((x) => (
                <option key={x} value={x}>
                  {t(x)}
                </option>
              ))}
            </select>
          );
        },
      },
    },
    {
      Key: 'requestedDate',
      Label: t('Request date'),
      width: '5%',
      Render: (row) => <FormattedDate value={row.requestedDate} variant="default" />,
    },
  ];

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  // return an alaska table
  return requests?.data ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        rows={requests.data.map((x) => {
          return { Key: x.id, ...x };
        })}
        totalRowCount={requests.totalRowCount}
        filteredRowCount={requests.filteredRowCount}
        scopes={requests.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        className="td-align-top"
        tableActionBarButtons={tableViewSwitcher}
        exportData={exportMyRequestData}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default RequestsMadeByMeGroupedList;
